import { useState } from 'react';
import * as R from 'ramda';
import { Helmet } from 'react-helmet-async';

import Button from '../buttons/Button';
import CheckmarkInCircle from '../svgs/CheckmarkInCircle';
import TextInput from '../inputs/TextInput';
import { Network } from '@wren/shared';
import InputError from '../inputs/InputError';
import { scale } from '../../util/scale';
import { Headings, Paragraph } from '../UI/TextStyles';
import { colors, spacing } from '../../style/theme';
import { getEmailValidationError } from '../../lib/validation';

const page = scale({
  width: '100%',
  margin: 'auto',
  textAlign: 'center',
  backgroundColor: 'white',
  paddingTop: [80, 120, 160],
  minHeight: [
    'calc(100vh - 80px)',
    'calc(100vh - 120px)',
    'calc(100vh - 160px)',
  ],
});

const container = scale({
  width: '100%',
  maxWidth: ['90%'],
  margin: 'auto',
  paddingBottom: spacing.xLarge,
});

const formStyle = scale({ margin: 'auto', maxWidth: ['95%', '600px'] });

const formContainer = scale({
  margin: 'auto',
  maxWidth: ['95%', '400px'],
});

const errorSpacing = scale({
  margin: 'auto',
  marginBottom: '20px',
  display: 'block',
});

export const requestPasswordResetEmail = async (
  email,
  setToast,
  setLoading
) => {
  const [response, responseBody] = await Network.post(
    'account/initiate-password-reset',
    {
      email,
    }
  );
  if (response.ok) {
    setToast({
      text: 'If the email you entered is in our system, a password reset email is on its way to your inbox.',
      type: 'success',
      isOnRight: true,
    });
  } else {
    setToast({
      text: responseBody.message,
      type: 'alert',
      isOnRight: true,
    });
  }
  if (setLoading) setLoading(false);
  return null;
};

const RequestPassReset = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submitAsync = async () => {
    setLoading(true);
    setError('');
    const [response, responseBody] = await Network.post(
      'account/initiate-password-reset',
      { email }
    );
    setLoading(false);
    if (response.ok) {
      setSubmitted(true);
    } else {
      setError(
        responseBody.message || 'Something went wrong, please try again.'
      );
    }
  };

  const clearErrors = () => {
    setLoading(false);
    setError('');
  };

  const checkForm = async (event) => {
    event.preventDefault();
    const error = getEmailValidationError(email);
    if (error) {
      setError(error);
      return;
    }
    if (!loading) {
      submitAsync();
    }
  };

  return (
    <div css={page}>
      <Helmet>
        <title>Wren | Request Password Reset</title>
      </Helmet>
      <div css={container}>
        <div css={formStyle}>
          <Headings.Large
            font="sans"
            weight={600}
            css={{
              margin: 'auto',
              textAlign: 'center',
              overflowWrap: 'break-word',
            }}
          >
            Reset your password
          </Headings.Large>
          {submitted ? (
            <div css={formContainer}>
              <Paragraph
                css={{ textAlign: 'center', marginBottom: spacing.medium }}
              >
                If the email you entered is in our system, a password reset
                email is on its way to your inbox.
              </Paragraph>
              <CheckmarkInCircle size={48} />
            </div>
          ) : (
            <>
              <Paragraph
                css={{ textAlign: 'center', marginBottom: spacing.medium }}
              >
                Enter your email address below to reset your password.
              </Paragraph>
              <div css={formContainer}>
                <form
                  autoComplete="on"
                  noValidate
                  onSubmit={(event) => checkForm(event)}
                >
                  <InputError error={error} injectCss={error && errorSpacing} />
                  <TextInput
                    handleInputChange={(answer) => {
                      setEmail(answer);
                      clearErrors();
                    }}
                    name="email"
                    type="email"
                    errorBorder={R.includes('Email', error)}
                    placeholder="Email"
                    autoComplete="email"
                    value={email}
                    label="Email address"
                    autoFocus
                  />
                  <Button
                    type="green"
                    size="large"
                    loading={loading}
                    submit
                    text="Send email"
                  />
                </form>
                <Paragraph
                  css={{
                    textAlign: 'center',
                    marginTop: spacing.medium,
                  }}
                >
                  If the email address doesn’t exist in our system, you will not
                  receive a reset email.
                  <br />
                  <br />
                  Reach out to{' '}
                  <a
                    href="mailto:support@wren.co"
                    css={{
                      color: colors.linkBlue,
                      textDecoration: 'none',
                    }}
                  >
                    support@wren.co
                  </a>{' '}
                  if you require any assistance.
                </Paragraph>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestPassReset;
